import React, { forwardRef, useContext } from "react";

import { DataProviderContext } from "src/core/provider/dataProvider";
import { SoftwareMode } from "src/gen/schema/models/fabric";

const SOFTWARE_UPGRADE_MODE_TOOLTIP: string =
  "Fabric is in software upgrade mode. Configuration changes for this fabric have been disabled until the software upgrade is completed.";

interface SoftwareUpgradeGuardProps {
  readonly ignoreSoftwareMode?: boolean;
  readonly disabled?: boolean;
  readonly tooltip?: string;
}

/**
 * Disables the wrapped component and shows a
 * tooltip when the fabric is in software upgrade
 * maintenance mode.
 *
 * Accepts an optional `ignoreSoftwareMode` prop
 * to bypass the check (e.g. for unbind actions
 * that should always be allowed).
 */
export const withSoftwareUpgradeGuard = <P extends SoftwareUpgradeGuardProps>(
  WrappedComponent: React.ComponentType<P>
) => {
  const Guarded = forwardRef<unknown, P>((props, ref) => {
    const { ignoreSoftwareMode, ...rest } = props;
    const { softwareConfig } = useContext(DataProviderContext);
    const disableBasedOnSoftwareMode = !(
      softwareConfig?.softwareMode !==
        SoftwareMode.SOFTWARE_MODE_MAINTENANCE_ON || ignoreSoftwareMode
    );
    const disable = props.disabled || disableBasedOnSoftwareMode;
    const tooltip = disableBasedOnSoftwareMode
      ? SOFTWARE_UPGRADE_MODE_TOOLTIP
      : props.tooltip;

    return (
      <WrappedComponent
        {...(rest as P)}
        tooltip={tooltip}
        disabled={disable}
        ref={ref}
      />
    );
  });

  Guarded.displayName = `withSoftwareUpgradeGuard(${
    WrappedComponent.displayName ?? WrappedComponent.name ?? "Component"
  })`;

  return Guarded;
};
